// src/lib/api.js
//
// How every page in this dashboard should talk to the backend.
//
// Always a RELATIVE path. Several pages used to name https://maa.ycpay.in
// outright, which has two consequences neither of them wanted:
//
//   * every stack talks to the LIVE deployment. The parallel stack on
//     testserver.ycpay.in and anything a developer runs locally were reading
//     and writing production data, whatever they were pointed at.
//   * the request is cross-origin, so the browser does not attach the
//     platform's session cookie, and since the platform started requiring a
//     token those calls answer 401.
//
// nginx already routes /api to the right backend for whichever stack is
// serving the page — aiocr, leads, leads-ads, or the main broker API — so a
// relative path is both correct and shorter (frontend-source/nginx.conf).
//
// Note that `credentials` is set explicitly even though a same-origin request
// would send cookies anyway under the default of 'same-origin'. It is here to
// state the intent, so that a later edit adding an absolute URL fails loudly
// with a CORS error rather than quietly dropping the session.

const DEFAULT_TIMEOUT_MS = 20000;

/**
 * Fetch `path` under /api and return parsed JSON.
 *
 * `path` is relative to /api — pass "/templates", not "/api/templates".
 * Throws an Error whose message is fit to show an operator.
 */
export async function apiFetch(path, options = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(),
                           options.timeoutMs || DEFAULT_TIMEOUT_MS);

  try {
    const res = await fetch(`/api${path}`, {
      credentials: 'include',
      signal: controller.signal,
      ...options,
      headers: {
        Accept: 'application/json',
        // A FormData body must NOT get an explicit Content-Type: the browser
        // has to write its own multipart boundary, and a hard-coded value
        // makes the server see a body it cannot parse.
        ...(options.body && !(options.body instanceof FormData)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(options.headers || {}),
      },
    });

    const text = await res.text();
    let body;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { detail: text };
    }

    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('Your session has expired. Sign in again.');
      }
      throw new Error(describeError(body, res.status));
    }
    return body;
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('The request timed out. Try again.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Turn a FastAPI error body into one line.
 *
 * `detail` is a string for most of this platform's endpoints, but the Google
 * Ads routes raise it as a dict — {request_id, errors: [{message, field_path}]}
 * — and the leads services sometimes use {message, details}. Rendering those
 * with String() gives "[object Object]", which hides the only part that says
 * what actually went wrong.
 */
export function describeError(body, status) {
  const detail = body?.detail ?? body?.message;
  if (!detail) return `Request failed (HTTP ${status}).`;
  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail?.errors)) {
    const parts = detail.errors.map(e =>
      typeof e === 'string'
        ? e
        : [e.message, e.field_path && `(${e.field_path})`].filter(Boolean).join(' ')
    );
    if (parts.length) return parts.join('; ');
  }
  if (detail?.message) {
    return [detail.message, detail.details].filter(Boolean).join(' — ');
  }
  // FastAPI's own 422 shape: a list of {loc, msg, type}.
  if (Array.isArray(detail)) {
    const parts = detail.map(e => (typeof e === 'string' ? e : e?.msg)).filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  try {
    return JSON.stringify(detail);
  } catch {
    return `Request failed (HTTP ${status}).`;
  }
}
